import { useMemo, useState } from "react";

import orderBy from "lodash/orderBy";
import { AppProps } from "next/app";

import { Queries } from "@api/graphql/queries/Queries";
import { listPublications, listPublicationsVariables, OrderDirection, Publication, PublicationSortField } from "@api/graphql/types";
import { ssrRequest } from "@api/GraphQLClient";
import { useLoctool } from "@bigfish/react-loctool";
import { StyledHighlightedCard } from "@components/Card/HighlightedProductCard";
import { ProductCardGrid, PublicationCard } from "@components/Card/ProductCard";
import { CTSScript } from "@components/CTS/CTSScript";
import { MagazinesContainer, MagazinesHeader, MagazinesPage as StyledMagazinesPage } from "@components/Magazines/Elements";
import { MagazinesSelect } from "@components/Magazines/Select";
import { Meta } from "@components/Meta";
import { CACHE_TIMEOUTS } from "@utils/Constants";

interface InitialProps {
    publications: Publication[];
}

type Props = InitialProps & AppProps;

const MagazinesPage = ({ publications }: Props) => {
    const Intl = useLoctool();
    const [sortOrder, setSortOrder] = useState<"asc" | "desc">("asc");
    const sortedPublications = useMemo(() => {
        return orderBy(publications, [publication => publication.name.toLowerCase()], [sortOrder]);
    }, [publications, sortOrder]);

    return (
        <>
            <CTSScript />
            <Meta />
            <StyledMagazinesPage className="_ce_measure_row">
                <div className="_ce_measure_column">
                    <MagazinesHeader
                        smallImage="/images/magazines-header-2025-11-tinified.jpg"
                        image="/images/magazines-header-2025-11-tinified.jpg"
                        uptitle={Intl.formatMessage({ id: "pages.magazines.header.subtitle" })}
                        title={Intl.formatMessage({ id: "pages.magazines.header.title" })}
                        lead={Intl.formatHtmlMessage({ id: "pages.magazines.header.lead" })}
                        className="_ce_measure_widget"
                        dataAttribute="magazinok-fejlec"
                    />
                    <MagazinesContainer className="_ce_measure_widget" data-ce-measure-widget="magazinok-termekek-lista">
                        <MagazinesSelect value={sortOrder} onChange={setSortOrder} />
                        <ProductCardGrid>
                            {sortedPublications?.map(publication => {
                                return publication.is_featured ? (
                                    <StyledHighlightedCard
                                        key={publication.id}
                                        titleTag="h2"
                                        publication={publication}
                                        className="_ce_measure_widget _ce_measure_link"
                                    />
                                ) : (
                                    <PublicationCard key={publication.id} titleTag="h2" publication={publication} />
                                );
                            })}
                        </ProductCardGrid>
                    </MagazinesContainer>
                </div>
            </StyledMagazinesPage>
        </>
    );
};

export async function getServerSideProps() {
    const { data } = await ssrRequest<listPublications, listPublicationsVariables>(
        {
            query: Queries.listPublications,
            variables: { first: 50, sortBy: { field: PublicationSortField.NAME, direction: OrderDirection.ASC }, filters: { is_listed: true } },
        },
        { cacheTimeout: CACHE_TIMEOUTS.magazinList },
    );
    if (data) {
        return {
            props: {
                publications: data.listPublications?.data.filter(publication => !!publication.products.length),
            },
        };
    }
    return {
        notFound: true,
    };
}

export default MagazinesPage;
